import { useEffect, useLayoutEffect, useState, type MutableRefObject, type RefObject } from 'react';
import { isEditableTarget, isInsideDialog, isSidebarShortcut } from '@/lib/sidebarState';

/** The rail exists from Tailwind's `md` up; below it the menu is a sheet. */
export const DESKTOP_QUERY = '(min-width: 768px)';

/**
 * The state the rail shows right now.
 *
 * Every page renders its own Layout, so a page change mounts a brand-new
 * rail. Mounted straight in the new state it would jump (CSS only animates
 * changes). So a new rail starts from what the previous one showed and
 * switches after the browser resolved that width: the switch to Verläufe
 * glides, a page change that keeps the state and the first load (nothing
 * shown yet) do not move at all.
 */
export function useRailHandoff(
  collapsed: boolean,
  shownRef: MutableRefObject<boolean | null>,
  railRef: RefObject<HTMLElement>,
): boolean {
  const [shown, setShown] = useState(() => shownRef.current ?? collapsed);

  useLayoutEffect(() => {
    if (shown === collapsed) return;
    // Forces style + layout of the old width, so the class change below is a
    // transition from it and not a jump. Still before the first paint.
    railRef.current?.getBoundingClientRect();
    setShown(collapsed);
  }, [collapsed, shown, railRef]);

  useEffect(() => {
    shownRef.current = shown;
  }, [shown, shownRef]);

  // Gone without a successor (logout, a page without sidebar): the next rail
  // is a first load again and must not glide. On a page change the new rail
  // has already read the value while rendering, before this cleanup runs.
  useEffect(
    () => () => {
      shownRef.current = null;
    },
    [shownRef],
  );

  return shown;
}

/** ⌘B / Ctrl+B folds the rail — not while typing, not in a dialog, not on the phone. */
export function useSidebarShortcut(toggle: () => void): void {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || !isSidebarShortcut(event)) return;
      if (isEditableTarget(event.target) || isInsideDialog(event.target)) return;
      if (!window.matchMedia(DESKTOP_QUERY).matches) return;
      event.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);
}
